import React, { useEffect, useState } from "react";
import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip, Slider } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FieldValue, Submission } from "../../models/submission.model";
import { useAuthenticatedFetch } from "../../utils/auth-fetch";
import { CheckCircle2Icon, ChevronDownIcon, ChevronUpIcon, FileDownIcon, Trash2Icon } from "lucide-react";
import ConfirmationDialog from "../../confirmation-dialog/confirmation-dialog";
import { useSnackbar } from "../../contexts/SnackbarContext";

const headerColor: string = "#424242";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.common.white,
  fontWeight: "bold",
  minWidth: "20rem",
  maxWidth: "20rem",
}));

const StyledSlider = styled(Slider)({
  minWidth: "16rem",
  maxWidth: "16rem",

  "& .MuiSlider-thumb": {
    backgroundColor: headerColor,
  },
  "& .MuiSlider-rail": {
    color: headerColor,
  },
  "& .MuiSlider-track": {
    color: headerColor,
  },
});

const StyledTableContainer = styled(TableContainer)({
  overflowX: "auto",
  "&::-webkit-scrollbar": {
    height: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: headerColor,
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#0000d6",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#f0f0f0",
  },
  scrollbarColor: `${headerColor} #f0f0f0`,
  scrollbarWidth: "thin",
});

function Statistics() {
  const [forms, setForms] = useState<{ id: string; title: string }[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<string>("");
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [confirmClearFormDialogOpen, setConfirmClearFormDialogOpen] = useState(false);
  const [formIdForClear, setFormIdForClear] = useState<string>("");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [maxRating, setMaxRating] = useState<number[]>([]);

  const [rating, setRating] = useState<number[]>([]);

  const authenticatedFetch = useAuthenticatedFetch();
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    authenticatedFetch("/api/forms")
      .then((res) => res.json())
      .then((data) =>
        setForms(
          data.map((form: any) => {
            return { id: form._id, title: form.title };
          })
        )
      )
      .catch((error) => console.error("Error fetching forms:", error));
  }, []);

  const handleToggleRowExpansion = (rowId: string) => {
    setExpandedRows((prev) => {
      const updated = new Set(prev);
      if (updated.has(rowId)) {
        updated.delete(rowId);
      } else {
        updated.add(rowId);
      }
      return updated;
    });
  };

  const handleSelectChange = async (event: any) => {
    if (event.target == null) return;
    const formId = event.target.value as string;
    setSelectedFormId(formId);

    if (formId === "") {
      setSubmissions([]);
      return;
    }

    await authenticatedFetch(`/api/forms/${formId}/submissions`)
      .then((res) => res.json())
      .then((data) => {
        setSubmissions(data);
        const submissions = data as Submission[];
        const currentRatings = maxRating.slice();
        let submissionIndex = 0;
        for (const submission of submissions) {
          let maxRatingScore = 0;
          for (const value of submission.values) {
            if (!value.ratingLimits.isRatable) {
              continue;
            }
            if (value.ratingLimits.optionRatings !== undefined && value.ratingLimits.optionRatings.length > 0) {
              maxRatingScore += Math.max(...value.ratingLimits.optionRatings);
            } else {
              maxRatingScore += value.ratingLimits.upperLimit;
            }
          }
          currentRatings[submissionIndex] = maxRatingScore;
          submissionIndex++;
        }
        setMaxRating(currentRatings);
      });
  };

  const handleGrading = (formId: string, submission: Submission): void => {
    authenticatedFetch(`/api/forms/${formId}/submissions`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(submission),
    }).then((response) => {
      if (response.ok) {
        showSnackbar("Grading successful!", "success");
      } else {
        showSnackbar("There was an error while grading the submission!", "error");
      }
    });
  };

  const handleConfirm = async (confirmed: boolean) => {
    if (formIdForClear === "") {
      return;
    }
    if (confirmed) {
      await handleSubmissionsClear(formIdForClear);
      setFormIdForClear("");
    }
  };

  const handleSubmissionsClear = async (formId: string) => {
    authenticatedFetch(`/api/forms/${formId}/submissions`, {
      method: "DELETE",
    }).then(() => setSubmissions([]));
  };

  const handleSubmissionExport = async (formId: string) => {
    const response = await authenticatedFetch(`/api/forms/${formId}/submissions/export`);

    const contentDisposition = response.headers.get("Content-Disposition");
    const filename = contentDisposition ? contentDisposition.split("filename=")[1].replace(/["']/g, "") : "submissions.xlsx";

    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);

    document.body.appendChild(link);
    link.click();
    if (!link.parentNode) {
      return;
    }
    link.parentNode.removeChild(link);

    window.URL.revokeObjectURL(url);
  };

  const getLastSubmissionDate = (submissions: Submission[]): string => {
    if (submissions.length === 0) return "N/A";
    return new Date(Math.max(...submissions.map((submission) => new Date(submission.submittedAt).getTime()))).toLocaleString();
  };

  const updateRatings = (index: number, values: FieldValue[]) => {
    const updatedRating = [...rating];
    if (index >= submissions.length) {
      return;
    }

    updatedRating[index] = values.reduce((sum, obj) => {
      if (obj.rating >= 0) {
        return sum + obj.rating;
      } else {
        return sum;
      }
    }, 0);
    setRating(updatedRating);
  };

  const sumRating = (index: number, values: FieldValue[], fieldObj: FieldValue, newValue: number) => {
    fieldObj.rating = newValue;

    updateRatings(index, values);
  };

  const allValues = submissions.flatMap((submission) => submission.values);
  const uniqueFields = allValues.filter((value, index, self) => index === self.findIndex((v) => v.fieldId === value.fieldId && !v.ratingLimits.isRatable));

  return (
    <div className="p-6 bg-gray-100 min-h-screen" style={{ width: "calc(100vw - 16rem)" }}>
      {" "}
      <div className="mx-auto bg-white p-8 shadow-md rounded-lg w-full">
        {" "}
        <select
          className="w-full p-2 mb-8 border rounded"
          value={selectedFormId}
          onChange={(event) => {
            handleSelectChange(event);
          }}
        >
          {" "}
          <option value="">-- Select a Form --</option>{" "}
          {forms.map((form) => (
            <option key={form.id} value={form.id}>
              {" "}
              {form.title}{" "}
            </option>
          ))}{" "}
        </select>
        {selectedFormId && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <Typography variant="h6" className="text-gray-700 mb-4">
                Form Details
              </Typography>
              <div className="flex flex-row justify-center items-center">
                <Tooltip title="Export as XLSX">
                  <IconButton aria-label="export as xlsx" onClick={() => handleSubmissionExport(selectedFormId)}>
                    <FileDownIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Clear">
                  <IconButton
                    aria-label="clear"
                    onClick={() => {
                      setConfirmClearFormDialogOpen(true);
                      setFormIdForClear(selectedFormId);
                    }}
                  >
                    <Trash2Icon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <Typography variant="subtitle1" className="text-gray-600">
                  Number of Submissions:
                </Typography>
                <Typography variant="h6" className="text-gray-800">
                  {submissions.length}
                </Typography>
              </div>
              <div>
                <Typography variant="subtitle1" className="text-gray-600">
                  Last Submission Date:
                </Typography>
                <Typography variant="h6" className="text-gray-800">
                  {getLastSubmissionDate(submissions)}
                </Typography>
              </div>
            </div>

            <StyledTableContainer as={Paper}>
              <Table>
                <TableHead className="bg-gray-800">
                  <TableRow>
                    <TableCell></TableCell>
                    {uniqueFields.map((field) => (
                      <StyledTableCell key={field.fieldId}>{field.fieldName}</StyledTableCell>
                    ))}
                    <StyledTableCell>Submission ID</StyledTableCell>
                    <StyledTableCell>Submitted At</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {submissions.map((submission, submissionIndex) => (
                    <React.Fragment key={submission._id}>
                      <TableRow>
                        <TableCell className="min-w-16">
                          <IconButton
                            aria-label="expand-row"
                            onClick={() => {
                              handleToggleRowExpansion(submission._id);
                              updateRatings(submissionIndex, submission.values);
                            }}
                          >
                            {expandedRows.has(submission._id) ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          </IconButton>
                        </TableCell>
                        {submission.values.map((fieldObj) => {
                          const field = submission.values.find((val) => val.fieldId === fieldObj.fieldId && !fieldObj.ratingLimits.isRatable);
                          return field ? (<TableCell key={fieldObj.fieldId}>{field?.value.toString() || ""}</TableCell>) : null;
                        })}
                        <TableCell>{submission.submissionId}</TableCell>
                        <TableCell>{new Date(submission.submittedAt).toLocaleString()}</TableCell>
                      </TableRow>
                      {expandedRows.has(submission._id) && (
                        <TableRow>
                          <TableCell colSpan={uniqueFields.length + 2}>
                            <div className="flex flex-row gap-16 p-4 relative left-8">
                              <div className="flex flex-col justify-center items-center">
                                <Typography>
                                  {rating[submissionIndex]} / {maxRating[submissionIndex]}
                                </Typography>
                                <button type="button" onClick={() => handleGrading(selectedFormId, submission)} className="w-36 bg-orange-600 text-white py-2 m-2 px-4 rounded hover:bg-orange-700 float-end flex flex-row items-center gap-4">
                                  <CheckCircle2Icon></CheckCircle2Icon> <Typography>Grade</Typography>
                                </button>
                              </div>
                              {submission.values.map((fieldObj) =>
                                fieldObj.ratingLimits.isRatable ? (
                                  <div key={fieldObj.fieldId} className="col-span-1 text-center">
                                    <Typography variant="subtitle1" className="text-gray-600 mb-2">
                                      {fieldObj.fieldName}
                                    </Typography>
                                    {fieldObj.ratingLimits.optionRatings && fieldObj.ratingLimits.lowerLimit === 0 && fieldObj.ratingLimits.upperLimit === 0 && (
                                      <div className="min-w-64">
                                        <span>Auto-grade: {fieldObj.rating}</span>
                                      </div>
                                    )}
                                    {(fieldObj.ratingLimits.optionRatings === undefined || fieldObj.ratingLimits.optionRatings.length === 0) && (
                                      <StyledSlider
                                        defaultValue={0}
                                        min={fieldObj.ratingLimits.lowerLimit}
                                        max={fieldObj.ratingLimits.upperLimit}
                                        step={1}
                                        marks
                                        value={fieldObj.rating}
                                        valueLabelDisplay="auto"
                                        onChange={(e, val) => {
                                          sumRating(submissionIndex, submission.values, fieldObj, val as number);
                                        }}
                                      />
                                    )}
                                  </div>
                                ) : null
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </div>
        )}
      </div>
      <ConfirmationDialog
        open={confirmClearFormDialogOpen}
        onClose={() => {
          setConfirmClearFormDialogOpen(false);
          setFormIdForClear("");
        }}
        title="Are you sure?"
        description="This action cannot be undone."
        onConfirm={(event: boolean) => handleConfirm(event)}
      />
    </div>
  );
}

export default Statistics;
